import React, { useEffect, useMemo, useState } from "react";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import {
  FaPlus,
  FaUserPlus,
  FaFileExport,
  FaArrowLeft,
  FaEnvelope,
  FaPencilAlt,
  FaFilter,
  FaRegCalendarAlt,
  FaEllipsisV,
  FaTimes,
  FaRegUserCircle,
  FaUserFriends,
  FaShieldAlt,
  FaRegClock,
  FaMoneyCheckAlt,
  FaChevronLeft,
  FaChevronRight,
  FaUser,
  FaMapMarkerAlt,
  FaUserTie,
  FaRegCircle,
  FaCamera,
} from "react-icons/fa";
import "../styles/AgentsPage.css";
import "../styles/UserManagement.css";
import { useNavigate } from "react-router-dom";
import {
  deleteAgentByOrg,
  getAllOrgAgents,
  markAgentPaymentDone,
  createAgent,
} from "../api/api";
import { getUserData, getOrgId } from "../utils/authStorage";
import { useToast } from "../context/ToastContext";

/* ── helpers ── */
const initials = (name = "") =>
  name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() || "")
    .join("") || "AG";

const AVATAR_COLORS = ["#6366f1", "#0ea5e9", "#14b8a6", "#f59e0b", "#ec4899", "#8b5cf6"];
const colorFor = (str = "") => {
  let h = 0;
  for (let i = 0; i < str.length; i++) h = str.charCodeAt(i) + ((h << 5) - h);
  return AVATAR_COLORS[Math.abs(h) % AVATAR_COLORS.length];
};

const ROWS_PER_PAGE = 10;

const AGENT_CREATE_SCHEMA = Yup.object({
  name: Yup.string().required("Name is required"),
  email: Yup.string().email("Invalid email").required("Email is required"),
  region: Yup.string().required("Region is required"),
  phoneNumber: Yup.string()
    .matches(/^[0-9]{10,15}$/, "Invalid phone number")
    .required("Phone is required"),
  fatherName: Yup.string().required("Father name is required"),
  cnic: Yup.string()
    .matches(/^[0-9]{5}-[0-9]{7}-[0-9]{1}$/, "Invalid CNIC (00000-0000000-0)")
    .required("CNIC is required"),
  address: Yup.string().required("Address is required"),
  iban: Yup.string(),
  accountTitle: Yup.string(),
});

const AGENT_STEP_FIELDS = [
  ["name", "email", "region"],
  ["phoneNumber", "fatherName", "cnic", "address"],
  [],
];

/* ── dummy fallback (matches design) ── */
const FALLBACK_AGENTS = [
  { _id: "1", name: "Jordan Diaz", code: "AG-992384", cnic: "AG-992384", status: "active", merchants: 142, volume: "Rs 1,240,000", commission: "Rs 12,400", payout: "Rs 3,200", lastActive: "2 mins ago" },
  { _id: "2", name: "Sarah Chen", code: "AG-992341", cnic: "AG-992341", status: "pending", merchants: 12, volume: "Rs 82,500", commission: "Rs 825", payout: "Rs 0", lastActive: "14 hrs ago" },
  { _id: "3", name: "Marcus Thorne", code: "AG-992102", cnic: "AG-992102", status: "active", merchants: 310, volume: "Rs 4,850,000", commission: "Rs 48,500", payout: "Rs 15,200", lastActive: "Just now" },
];

const AgentsList = ({ isSidebarOpen }) => {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [loading, setLoading] = useState(true);
  const [rawAgents, setRawAgents] = useState([]);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [profileAgent, setProfileAgent] = useState(null);
  const [detailTab, setDetailTab] = useState("Activity");
  const [createAgentOpen, setCreateAgentOpen] = useState(false);
  const [agentStep, setAgentStep] = useState(0);
  const [agentPreviewPhoto, setAgentPreviewPhoto] = useState("");

  const userData = getUserData();
  const user = userData?.user || userData?.data || userData || {};
  const orgId = getOrgId() ?? user?.orgId ?? user?.profile?.orgId;

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await getAllOrgAgents(orgId);
        if (res?.data?.length) setRawAgents(res.data);
      } catch (err) {
        console.error("Error fetching agents:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [orgId]);

  const refreshAgents = async () => {
    try {
      const res = await getAllOrgAgents(orgId);
      if (res?.data?.length) setRawAgents(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  /* normalize agents for display, fall back to dummy when empty */
  const agents = useMemo(() => {
    const source = rawAgents.length ? rawAgents : FALLBACK_AGENTS;
    return source.map((a, i) => ({
      ...a,
      _id: a._id ?? a.cnic ?? String(i),
      name: a.name ?? "Unknown Agent",
      code: a.code ?? (a.cnic ? `AG-${String(a.cnic).slice(-6)}` : `AG-${1000 + i}`),
      status: a.status ?? (a.isPaymentRequested ? "pending" : "active"),
      merchants: a.merchants ?? a.totalMerchants ?? "—",
      volume: a.volume ?? "—",
      commission: a.commission ?? "—",
      payout: a.payout ?? a.duePayout ?? "—",
      lastActive: a.lastActive ?? "—",
    }));
  }, [rawAgents]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return agents;
    return agents.filter(
      (a) =>
        a.name.toLowerCase().includes(q) ||
        String(a.code).toLowerCase().includes(q)
    );
  }, [agents, search]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / ROWS_PER_PAGE));
  const currentPage = Math.min(page, totalPages);
  const pageRows = filtered.slice(
    (currentPage - 1) * ROWS_PER_PAGE,
    currentPage * ROWS_PER_PAGE
  );

  const totalAgents = rawAgents.length || 1284;
  const activeAgents =
    rawAgents.length
      ? agents.filter((a) => a.status === "active").length
      : 1152;

  const handleDeleteAgent = async (cnic, name) => {
    try {
      const res = await deleteAgentByOrg(cnic);
      if (res?.isSuccess) {
        showToast({ severity: "success", summary: "Deleted", detail: `${name} deleted`, life: 2000 });
        refreshAgents();
      } else {
        showToast({ severity: "error", summary: "Error", detail: res?.message || "Failed to delete agent", life: 3000 });
      }
    } catch {
      showToast({ severity: "error", summary: "Error", detail: "Something went wrong", life: 3000 });
    }
  };

  const handlePaymentDone = async (agent) => {
    try {
      const res = await markAgentPaymentDone(agent.cnic);
      if (res?.isSuccess) {
        showToast({ severity: "success", summary: "Paid", detail: "Payment completed", life: 2000 });
        refreshAgents();
      }
    } catch {
      showToast({ severity: "error", summary: "Error", detail: "Payment update failed", life: 3000 });
    }
  };

  const handleCreateAgent = async (values, { resetForm, setSubmitting }) => {
    try {
      const payload = { ...values, orgID: orgId };
      const res = await createAgent(payload);
      if (res?.isSuccess) {
        showToast({ severity: "success", summary: "Created", detail: res.message || "Agent created successfully", life: 2200 });
        resetForm();
        setAgentPreviewPhoto("");
        setCreateAgentOpen(false);
        setAgentStep(0);
        refreshAgents();
      } else {
        showToast({ severity: "error", summary: "Error", detail: res?.message || "Failed to create agent", life: 3000 });
      }
    } catch {
      showToast({ severity: "error", summary: "Error", detail: "Something went wrong", life: 3000 });
    } finally {
      setSubmitting(false);
    }
  };

  const kpis = [
    { label: "Total Agents", value: Number(totalAgents).toLocaleString(), icon: <FaUserFriends />, chip: "+12%", accent: "#6366f1" },
    { label: "Active Agents", value: Number(activeAgents).toLocaleString(), icon: <FaShieldAlt />, chip: "98%", accent: "#14b8a6" },
    { label: "Pending Payouts", value: "Rs 42,850", icon: <FaRegClock />, chip: "High", chipMuted: true, accent: "#f59e0b" },
    { label: "Total Commissions", value: "Rs 2.4M", icon: <FaMoneyCheckAlt />, chip: "+24%", accent: "#0ea5e9" },
  ];

  const detailActivities = selectedAgent
    ? [
        {
          when: "TODAY, 11:42 AM",
          title: `Onboarded Blue Wave Cafe`,
          desc: `Contract ${selectedAgent.code || "#CC-9921"} signed via E-Sign.`,
        },
        {
          when: "YESTERDAY, 4:15 PM",
          title: "Payout Request Approved",
          desc: `Withdrawal of ${selectedAgent.payout && selectedAgent.payout !== "—" ? selectedAgent.payout : "Rs 3,200"} processed to bank account.`,
        },
        {
          when: "2 DAYS AGO",
          title: "Support Ticket Resolved",
          desc: "Merchant ID #MA-102 verification issue cleared.",
        },
      ]
    : [];

  const profileBars = [28, 40, 32, 56, 44, 68, 60, 74, 65, 78];

  const profileRecentActivity = profileAgent
    ? [
        {
          title: `New Merchant Onboarded: ${profileAgent.name.split(" ")[0]} Logistics`,
          time: "Today at 10:42 AM",
          tag: `Action ID: ${profileAgent.code}`,
        },
        {
          title: `Commission Payout Approved: ${profileAgent.commission !== "—" ? profileAgent.commission : "Rs 4,250"}`,
          time: "Yesterday at 4:15 PM",
          tag: "Settled",
        },
      ]
    : [];

  const profileMerchants = profileAgent
    ? [
        {
          name: "Nebula Systems",
          type: "SaaS & Infrastructure",
          volume: "Rs 242K Vol",
          status: "Premier",
        },
        {
          name: "Caffeine Hub",
          type: "F&B / Retail",
          volume: "Rs 118K Vol",
          status: "Growing",
        },
      ]
    : [];

  if (loading) {
    return (
      <div className="ag-page">
        <p className="ag-muted">Loading agents…</p>
      </div>
    );
  }

  if (profileAgent) {
    return (
      <div className="ag-page ag-page--detail">
        <button
          type="button"
          className="ag-back"
          onClick={() => setProfileAgent(null)}
        >
          <FaArrowLeft /> Back to Agents
        </button>

        <div className="ag-profile-head">
          <div className="ag-profile-agent">
            <div className="ag-profile-avatar" style={{ background: colorFor(profileAgent.name) }}>
              {initials(profileAgent.name)}
            </div>
            <div>
              <h2>{profileAgent.name}</h2>
              <div className="ag-profile-meta">ID: {profileAgent.code} • <span>Active Status</span></div>
            </div>
          </div>

          <div className="ag-profile-actions">
            <button className="ag-btn" type="button">
              <FaEnvelope /> Message Agent
            </button>
            <button className="ag-btn" type="button">
              <FaPencilAlt /> Edit Profile
            </button>
            <button className="ag-btn ag-btn--primary" type="button">
              <FaFileExport /> Export Data
            </button>
          </div>
        </div>

        <div className="ag-profile-stats">
          <div className="ag-profile-stat">
            <label>Merchant Count</label>
            <h3>{profileAgent.merchants !== "—" ? profileAgent.merchants : 142}</h3>
            <p>↗ +12% this mo</p>
          </div>
          <div className="ag-profile-stat">
            <label>Monthly Volume</label>
            <h3>{profileAgent.volume !== "—" ? profileAgent.volume : "Rs 1.2M"}</h3>
            <p>↗ +8.4%</p>
          </div>
          <div className="ag-profile-stat">
            <label>Total Revenue</label>
            <h3>Rs 4.5M</h3>
            <p>→ Stable</p>
          </div>
          <div className="ag-profile-stat">
            <label>Commission Earned</label>
            <h3>{profileAgent.commission !== "—" ? profileAgent.commission : "Rs 42.5K"}</h3>
            <p>↗ Top 1%</p>
          </div>
          <div className="ag-profile-stat ag-profile-stat--ring">
            <label>Collection Perf.</label>
            <h3>98.4%</h3>
            <p>◎ Elite</p>
            <span className="ag-profile-ring" />
          </div>
        </div>

        <div className="ag-profile-grid">
          <div className="ag-profile-maincol">
            <div className="ag-profile-card ag-profile-card--chart">
              <div className="ag-profile-card__head">
                <div>
                  <h4>Performance Growth</h4>
                  <span>Net revenue vs commission across 12 months</span>
                </div>
              </div>

              <div className="ag-growth">
                <div className="ag-growth__bars">
                  {profileBars.map((bar, idx) => (
                    <div key={idx} className="ag-growth__bar" style={{ height: `${bar}%` }} />
                  ))}
                </div>
                <svg className="ag-growth__line" viewBox="0 0 1000 240" preserveAspectRatio="none">
                  <path d="M0,220 C60,200 120,210 180,205 C250,198 300,210 360,178 C430,138 500,92 580,102 C660,112 720,130 790,110 C860,90 920,30 1000,26" />
                </svg>
              </div>
            </div>

            <div className="ag-profile-card">
              <div className="ag-profile-card__head ag-profile-card__head--spread">
                <h4>Merchant Portfolio</h4>
                <button className="ag-inline-btn" type="button">View All 142 Merchants</button>
              </div>
              <div className="ag-merchant-tiles">
                {profileMerchants.map((merchant) => (
                  <div key={merchant.name} className="ag-merchant-tile">
                    <div>
                      <strong>{merchant.name}</strong>
                      <span>{merchant.type}</span>
                    </div>
                    <div className="ag-merchant-meta">
                      <em>{merchant.volume}</em>
                      <small>{merchant.status}</small>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="ag-profile-card">
              <div className="ag-profile-card__head">
                <h4>Recent Activity</h4>
              </div>
              <div className="ag-profile-activity">
                {profileRecentActivity.map((activity) => (
                  <div key={activity.title} className="ag-profile-activity__row">
                    <div>
                      <strong>{activity.title}</strong>
                      <span>{activity.time}</span>
                    </div>
                    <small>{activity.tag}</small>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="ag-profile-sidecol">
            <div className="ag-profile-card ag-profile-card--invoice">
              <div className="ag-profile-card__head">
                <div>
                  <h4>Invoice Preview</h4>
                  <span>INV-2024-05-SC</span>
                </div>
              </div>
              <div className="ag-invoice">
                <div className="ag-invoice__row"><span>Merchant</span><b>MerchantFlow Inc.</b></div>
                <div className="ag-invoice__row"><span>Bill To</span><b>{profileAgent.name}</b></div>
                <div className="ag-invoice__row"><span>Rate</span><b>2.5%</b></div>
                <div className="ag-invoice__row"><span>Earned</span><b>Rs 22,400</b></div>
                <div className="ag-invoice__total"><span>Total Payable</span><b>Rs 44,000</b></div>
              </div>
              <button className="ag-btn ag-btn--primary ag-fullbtn" type="button">Release Payout</button>
            </div>

            <div className="ag-profile-card ag-profile-card--settlements">
              <div className="ag-profile-card__head">
                <h4>Pending Settlements</h4>
              </div>
              <div className="ag-settlement-row">
                <div>
                  <strong>June Cycle #1</strong>
                  <span>Est: Rs 12,450</span>
                </div>
                <small>In 4 Days</small>
              </div>
              <div className="ag-settlement-row">
                <div>
                  <strong>June Cycle #2</strong>
                  <span>Est: Rs 14,100</span>
                </div>
                <small>In 18 Days</small>
              </div>
            </div>

            <div className="ag-profile-card ag-profile-card--insight">
              <h4>Strategic Insight</h4>
              <p>
                {profileAgent.name.split(" ")[0]} has a <b>94% conversion rate</b> on premium merchants. Recommend assigning
                upcoming enterprise pipeline for maximum yield.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="ag-page">
      {/* Header */}
      <div className="ag-head">
        <div>
          <h1 className="ag-head__title">Agents</h1>
          <div className="ag-head__sub">
            Manage your global network of financial representatives.
          </div>
        </div>
        <div className="ag-head__actions">
          <button className="ag-btn" type="button">
            <FaFileExport /> Export
          </button>
          <button
            className="ag-btn ag-btn--primary"
            type="button"
            onClick={() => { setCreateAgentOpen(true); setAgentStep(0); }}
          >
            <FaUserPlus /> Create Agent
          </button>
        </div>
      </div>

      {/* KPI cards */}
      <div className="ag-kpis">
        {kpis.map((k) => (
          <div key={k.label} className="ag-kpi">
            <div className="ag-kpi__top">
              <div className="ag-kpi__icon" style={{ color: k.accent }}>{k.icon}</div>
              <div className={`ag-kpi__chip ${k.chipMuted ? "ag-kpi__chip--muted" : ""}`}>{k.chip}</div>
            </div>
            <div className="ag-kpi__label">{k.label}</div>
            <div className="ag-kpi__value">{k.value}</div>
            <div className="ag-kpi__bar" style={{ background: k.accent }} />
          </div>
        ))}
      </div>

      {/* Table card */}
      <div className="ag-tablecard">
        <div className="ag-toolbar">
          <div className="ag-toolbar__left">
            <button className="ag-pillbtn" type="button">
              <FaFilter /> Filters
            </button>
            <button className="ag-pillbtn" type="button">
              <FaRegCalendarAlt /> Last 30 Days
            </button>
            <input
              className="ag-pillbtn"
              style={{ minWidth: 200, color: "#fff" }}
              placeholder="Search agents, IDs, or merchants…"
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            />
          </div>
          <div className="ag-toolbar__right">
            <span className="ag-selinfo">Selected: <b>0</b></span>
            <button className="ag-pillbtn" type="button">Bulk Actions</button>
          </div>
        </div>

        <div className="ag-tablewrap">
          <table className="ag-table">
            <thead>
              <tr>
                <th style={{ width: 44 }}><input type="checkbox" className="ag-check" /></th>
                <th>Agent</th>
                <th>Status</th>
                <th>Merchants</th>
                <th>Volume</th>
                <th>Commission</th>
                <th>Due Payout</th>
                <th>Last Active</th>
                <th style={{ width: 44 }}></th>
              </tr>
            </thead>
            <tbody>
              {pageRows.length === 0 ? (
                <tr>
                  <td colSpan={9} className="ag-muted" style={{ textAlign: "center", padding: 30 }}>
                    No agents found.
                  </td>
                </tr>
              ) : (
                pageRows.map((a) => (
                  <tr key={a._id}>
                    <td><input type="checkbox" className="ag-check" /></td>
                    <td>
                      <div className="ag-agent">
                        <div className="ag-avatar" style={{ background: colorFor(a.name) }}>
                          {initials(a.name)}
                        </div>
                        <div>
                          <button
                            type="button"
                            className="ag-agent__link"
                            onClick={() => {
                              setProfileAgent(a);
                              setSelectedAgent(null);
                            }}
                          >
                            {a.name}
                          </button>
                          <div className="ag-agent__id">{a.code}</div>
                        </div>
                      </div>
                    </td>
                    <td>
                      <span className={`ag-status ag-status--${a.status === "pending" ? "pending" : a.status === "inactive" ? "inactive" : "active"}`}>
                        {a.status}
                      </span>
                    </td>
                    <td>{a.merchants}</td>
                    <td>{a.volume}</td>
                    <td className="ag-commission">{a.commission}</td>
                    <td className={a.payout === "Rs 0" || a.payout === "—" ? "ag-muted" : "ag-payout"}>{a.payout}</td>
                    <td className="ag-muted">{a.lastActive}</td>
                    <td>
                      <button
                        className="ag-rowmenu"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedAgent(a);
                          setDetailTab("Activity");
                        }}
                      >
                        <FaEllipsisV />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Footer */}
        <div className="ag-foot">
          <div className="ag-foot__info">
            Showing <b>{filtered.length ? (currentPage - 1) * ROWS_PER_PAGE + 1 : 0}-
            {Math.min(currentPage * ROWS_PER_PAGE, filtered.length)}</b> of{" "}
            <b>{Number(totalAgents).toLocaleString()}</b> agents
          </div>
          <div className="ag-pager">
            <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={currentPage === 1}>
              <FaChevronLeft />
            </button>
            {Array.from({ length: Math.min(3, totalPages) }, (_, i) => i + 1).map((n) => (
              <button key={n} className={n === currentPage ? "active" : ""} onClick={() => setPage(n)}>
                {n}
              </button>
            ))}
            {totalPages > 3 && <button disabled>…</button>}
            <button onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}>
              <FaChevronRight />
            </button>
          </div>
        </div>
      </div>

      {/* Floating add (mobile convenience) */}
      <button
        className="ag-btn ag-btn--primary"
        style={{ display: "none" }}
        onClick={() => navigate("/dashboard/agents?mode=create")}
      >
        <FaPlus />
      </button>

      {createAgentOpen && (
        <div className="um-modaloverlay" role="dialog" aria-modal="true" aria-label="Create Agent">
          <button
            type="button"
            className="um-modaloverlay__scrim"
            aria-label="Close"
            onClick={() => { setCreateAgentOpen(false); setAgentStep(0); setAgentPreviewPhoto(""); }}
          />
          <Formik
            initialValues={{ name: "", email: "", region: "", phoneNumber: "", fatherName: "", cnic: "", address: "", iban: "", accountTitle: "" }}
            validationSchema={AGENT_CREATE_SCHEMA}
            onSubmit={handleCreateAgent}
            validateOnChange={false}
            validateOnBlur
          >
            {({ values, errors, touched, setFieldValue, setTouched, validateForm, submitForm, isSubmitting }) => (
              <Form
                className="um-adddialog-shell um-adddialog-shell--overlay"
                onKeyDown={(e) => { if (e.key === "Enter") e.preventDefault(); }}
              >
                <div className="um-adddialog">

                  {/* Header */}
                  <div className="um-adddialog__header">
                    <div>
                      <h2 className="um-adddialog__title">Add Agent</h2>
                      <div className="um-adddialog__subtitle">Onboard a new field representative to your organization.</div>
                    </div>
                    <button
                      type="button"
                      className="um-adddialog__close"
                      onClick={() => { setCreateAgentOpen(false); setAgentStep(0); setAgentPreviewPhoto(""); }}
                    >
                      <FaTimes />
                    </button>
                  </div>

                  {/* Stepper */}
                  <div className="um-adddialog__steps" style={{ gridTemplateColumns: "repeat(3, 1fr)" }}>
                    {[["Basic Info", 0], ["Identity", 1], ["Banking", 2]].map(([label, idx]) => (
                      <div key={label} className={`um-adddialog__step ${agentStep === idx ? "is-active" : ""}`}>
                        <span className="um-adddialog__stepIndex">{idx + 1}</span>
                        {label}
                      </div>
                    ))}
                  </div>

                  {/* Body */}
                  <div className="um-adddialog__body">

                    {/* Step 0 — Basic Info */}
                    {agentStep === 0 && (
                      <div className="um-adddialog__grid">
                        <div className="um-adddialog__field um-adddialog__field--full" style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 8, marginBottom: 4 }}>
                          <div
                            style={{
                              width: 72, height: 72, borderRadius: "50%",
                              background: agentPreviewPhoto ? "transparent" : "rgba(99,102,241,0.18)",
                              border: "2px dashed rgba(99,102,241,0.4)",
                              display: "flex", alignItems: "center", justifyContent: "center",
                              overflow: "hidden", flexShrink: 0,
                            }}
                          >
                            {agentPreviewPhoto
                              ? <img src={agentPreviewPhoto} alt="preview" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                              : <FaCamera style={{ fontSize: 22, color: "rgba(99,102,241,0.7)" }} />
                            }
                          </div>
                          <label
                            htmlFor="ag-dialog-photo"
                            style={{
                              cursor: "pointer", fontSize: 11, fontWeight: 700,
                              letterSpacing: "0.06em", color: "rgba(129,140,248,0.9)",
                              background: "rgba(99,102,241,0.12)",
                              border: "1px solid rgba(99,102,241,0.25)",
                              borderRadius: 6, padding: "4px 14px",
                            }}
                          >
                            {agentPreviewPhoto ? "Change Photo" : "Upload Photo"}
                          </label>
                          <input
                            id="ag-dialog-photo"
                            type="file"
                            accept="image/*"
                            hidden
                            onChange={(e) => {
                              const file = e.target.files?.[0];
                              if (file) setAgentPreviewPhoto(URL.createObjectURL(file));
                            }}
                          />
                          {agentPreviewPhoto && (
                            <button
                              type="button"
                              style={{ background: "none", border: "none", fontSize: 11, color: "rgba(195,197,217,0.6)", cursor: "pointer", padding: 0 }}
                              onClick={() => setAgentPreviewPhoto("")}
                            >
                              Remove
                            </button>
                          )}
                        </div>
                        <div className="um-adddialog__field">
                          <label className="um-adddialog__label">AGENT NAME</label>
                          <div className="um-forminputwrap">
                            <FaUser className="um-forminputwrap__icon" />
                            <Field name="name" type="text" className="um-adddialog__input" placeholder="e.g. Jordan Diaz" />
                          </div>
                          {touched.name && errors.name ? <div className="error-text">{errors.name}</div> : <div className="error-text" />}
                        </div>

                        <div className="um-adddialog__field">
                          <label className="um-adddialog__label">REGION</label>
                          <div className="um-forminputwrap">
                            <FaMapMarkerAlt className="um-forminputwrap__icon" />
                            <Field name="region" type="text" className="um-adddialog__input" placeholder="e.g. Karachi South" />
                          </div>
                          {touched.region && errors.region ? <div className="error-text">{errors.region}</div> : <div className="error-text" />}
                        </div>

                        <div className="um-adddialog__field um-adddialog__field--full">
                          <label className="um-adddialog__label">ENTERPRISE EMAIL</label>
                          <div className="um-forminputwrap">
                            <FaEnvelope className="um-forminputwrap__icon" />
                            <Field name="email" type="email" className="um-adddialog__input" placeholder="agent@company.com" />
                          </div>
                          {touched.email && errors.email ? <div className="error-text">{errors.email}</div> : <div className="error-text" />}
                        </div>
                      </div>
                    )}

                    {/* Step 1 — Identity */}
                    {agentStep === 1 && (
                      <div className="um-adddialog__grid">
                        <div className="um-adddialog__field">
                          <label className="um-adddialog__label">PHONE NUMBER</label>
                          <div className="um-adddialog__field um-adddialog__field--nomargin">
                            <PhoneInput
                              country="pk"
                              value={values.phoneNumber}
                              onChange={(value) => setFieldValue("phoneNumber", value.replace(/\D/g, ""))}
                              inputClass="um-adddialog__input"
                              countryCodeEditable={false}
                            />
                          </div>
                          {touched.phoneNumber && errors.phoneNumber ? <div className="error-text">{errors.phoneNumber}</div> : <div className="error-text" />}
                        </div>

                        <div className="um-adddialog__field">
                          <label className="um-adddialog__label">FATHER NAME</label>
                          <div className="um-forminputwrap">
                            <FaUserTie className="um-forminputwrap__icon" />
                            <Field name="fatherName" type="text" className="um-adddialog__input" placeholder="Father's full name" />
                          </div>
                          {touched.fatherName && errors.fatherName ? <div className="error-text">{errors.fatherName}</div> : <div className="error-text" />}
                        </div>

                        <div className="um-adddialog__field">
                          <label className="um-adddialog__label">CNIC</label>
                          <div className="um-forminputwrap">
                            <FaRegCircle className="um-forminputwrap__icon" />
                            <input
                              className="um-adddialog__input"
                              placeholder="00000-0000000-0"
                              maxLength={15}
                              value={values.cnic}
                              onChange={(e) => {
                                let v = e.target.value.replace(/[^0-9]/g, "");
                                if (v.length > 5) v = v.slice(0, 5) + "-" + v.slice(5);
                                if (v.length > 13) v = v.slice(0, 13) + "-" + v.slice(13, 14);
                                setFieldValue("cnic", v);
                              }}
                            />
                          </div>
                          {touched.cnic && errors.cnic ? <div className="error-text">{errors.cnic}</div> : <div className="error-text" />}
                        </div>

                        <div className="um-adddialog__field">
                          <label className="um-adddialog__label">ADDRESS</label>
                          <div className="um-forminputwrap">
                            <FaMapMarkerAlt className="um-forminputwrap__icon" />
                            <Field name="address" type="text" className="um-adddialog__input" placeholder="Street, City" />
                          </div>
                          {touched.address && errors.address ? <div className="error-text">{errors.address}</div> : <div className="error-text" />}
                        </div>
                      </div>
                    )}

                    {/* Step 2 — Banking */}
                    {agentStep === 2 && (
                      <div className="um-adddialog__grid">
                        <div className="um-adddialog__field um-adddialog__field--full">
                          <label className="um-adddialog__label">IBAN</label>
                          <div className="um-forminputwrap">
                            <FaRegCircle className="um-forminputwrap__icon" />
                            <Field name="iban" type="text" className="um-adddialog__input" placeholder="PK00ABCD0123456789" />
                          </div>
                          {touched.iban && errors.iban ? <div className="error-text">{errors.iban}</div> : <div className="error-text" />}
                        </div>

                        <div className="um-adddialog__field um-adddialog__field--full">
                          <label className="um-adddialog__label">ACCOUNT TITLE</label>
                          <div className="um-forminputwrap">
                            <FaUserTie className="um-forminputwrap__icon" />
                            <Field name="accountTitle" type="text" className="um-adddialog__input" placeholder="Account holder name" />
                          </div>
                          {touched.accountTitle && errors.accountTitle ? <div className="error-text">{errors.accountTitle}</div> : <div className="error-text" />}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Footer */}
                  <div className="um-adddialog__footer">
                    <button
                      type="button"
                      className="um-adddialog__cancel"
                      onClick={() => { setCreateAgentOpen(false); setAgentStep(0); setAgentPreviewPhoto(""); }}
                    >
                      Cancel
                    </button>
                    <div className="um-adddialog__actions">
                      {agentStep > 0 && (
                        <button
                          type="button"
                          className="um-adddialog__back"
                          onClick={() => setAgentStep((s) => s - 1)}
                        >
                          Back
                        </button>
                      )}
                      {agentStep < 2 ? (
                        <button
                          key="ag-next"
                          type="button"
                          className="um-adddialog__submit"
                          onClick={async () => {
                            const fields = AGENT_STEP_FIELDS[agentStep];
                            const touchObj = {};
                            fields.forEach((f) => { touchObj[f] = true; });
                            setTouched(touchObj, false);
                            const errs = await validateForm();
                            if (fields.every((f) => !errs[f])) setAgentStep((s) => s + 1);
                          }}
                        >
                          Next →
                        </button>
                      ) : (
                        <button
                          key="ag-submit"
                          type="button"
                          className="um-adddialog__submit"
                          disabled={isSubmitting}
                          onClick={() => submitForm()}
                        >
                          {isSubmitting ? "Creating..." : "Create Agent"}
                        </button>
                      )}
                    </div>
                  </div>

                </div>
              </Form>
            )}
          </Formik>
        </div>
      )}

      {selectedAgent && (
        <div className="ag-details-overlay" onClick={() => setSelectedAgent(null)}>
          <aside className="ag-details" onClick={(e) => e.stopPropagation()}>
            <div className="ag-details__head">
              <div className="ag-details__title"><FaRegUserCircle /> Agent Details</div>
              <button className="ag-details__close" onClick={() => setSelectedAgent(null)}>
                <FaTimes />
              </button>
            </div>

            <div className="ag-details__body">
              <div className="ag-details__profile">
                <div className="ag-details__avatar" style={{ background: colorFor(selectedAgent.name) }}>
                  {initials(selectedAgent.name)}
                </div>
                <div>
                  <div className="ag-details__name">{selectedAgent.name}</div>
                  <div className="ag-details__meta">Senior Field Agent • Miami, FL</div>
                  <span className="ag-details__tag">Top Performer</span>
                </div>
              </div>

              <div className="ag-details__stats">
                <div className="ag-details__stat">
                  <div className="ag-details__stat-label">Lifetime Revenue</div>
                  <div className="ag-details__stat-value">{selectedAgent.volume && selectedAgent.volume !== "—" ? selectedAgent.volume : "Rs 12.4M"}</div>
                </div>
                <div className="ag-details__stat">
                  <div className="ag-details__stat-label">Conversion Rate</div>
                  <div className="ag-details__stat-value ag-details__stat-value--mint">64.2%</div>
                </div>
              </div>

              <div className="ag-details__tabs">
                {["Activity", "Merchants", "Compliance"].map((tab) => (
                  <button
                    key={tab}
                    className={`ag-details__tab ${detailTab === tab ? "active" : ""}`}
                    onClick={() => setDetailTab(tab)}
                  >
                    {tab}
                  </button>
                ))}
              </div>

              {detailTab === "Activity" && (
                <div className="ag-details__timeline">
                  {detailActivities.map((item, idx) => (
                    <div key={item.title} className="ag-details__event">
                      <span className={`ag-details__dot ${idx === 0 ? "active" : ""}`} />
                      <div>
                        <div className="ag-details__when">{item.when}</div>
                        <div className="ag-details__event-title">{item.title}</div>
                        <div className="ag-details__event-desc">{item.desc}</div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {detailTab === "Merchants" && (
                <div className="ag-details__placeholder">Merchant portfolio details available in View Merchants.</div>
              )}

              {detailTab === "Compliance" && (
                <div className="ag-details__placeholder">KYC and compliance checks are up to date.</div>
              )}

              <div className="ag-details__actions">
                <button
                  className="ag-details__btn ag-details__btn--primary"
                  onClick={() => navigate("/dashboard/agent-earnings", { state: { user: selectedAgent, name: selectedAgent.name } })}
                >
                  Initiate Message
                </button>
                <button
                  className="ag-details__btn ag-details__btn--ghost"
                  onClick={() => {
                    if (window.confirm(`Suspend account for ${selectedAgent.name}?`)) {
                      handleDeleteAgent(selectedAgent.cnic, selectedAgent.name);
                      setSelectedAgent(null);
                    }
                  }}
                >
                  Suspend Account
                </button>
              </div>
            </div>
          </aside>
        </div>
      )}
    </div>
  );
};

export default AgentsList;
